// src/components/home/TodayPanel.jsx
//
// Cross-team "today" panel for multi-team coaches on Home. Renders
// above the team picker. For each coach team that has an urgent
// pending action, shows one compact row with the team name, the
// most-urgent action, and a tap-target to that team's page.
//
// Why this exists: a coach with 3-5 teams on TeamSnap-style apps
// has to tap into each team to see what needs attention. The
// TodayPanel does the legwork — surfaces the most pressing thing
// per team in one glance.
//
// Single-team users never see this because Smart Bypass routes
// them straight to their team page from /dashboard. Multi-team
// users see the panel above the team picker on /dashboard.
// Pure parents (no coach teams) also don't see it.
//
// Architecture: each row is its OWN subcomponent with its own
// Firestore subscription. This avoids a god-hook + lets the panel
// gracefully render only the rows that actually have actions.
// A row renders null if its team has nothing urgent — the parent
// panel renders null if no rows rendered.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit, onSnapshot, getDocs,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { endOfSeasonDay } from '@/utils/seasonDates';
import {
  CalendarDaysIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  ArrowRightIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ────────────────────────────────────────────────────────────────
 * Per-team summary subscription. Returns the most urgent action
 * (or null if nothing pending) without exposing intermediate state
 * to the parent. Same priority order as TeamSmartActions:
 *   weather > closing reg > unpaid > recap due > missing docs
 * but we only surface one per team to keep the panel scannable.
 * ─────────────────────────────────────────────────────────────── */
function useTeamUrgentAction(teamId) {
  const [action, setAction] = useState(null);

  useEffect(() => {
    if (!teamId) return undefined;

    let activeSeason = null;
    let unpaidCount = 0;
    let daysUntilClose = null;
    let recentGame = null;

    const recompute = () => {
      // 1. Reg closing soon (≤7 days, not closed)
      if (
        activeSeason &&
        daysUntilClose !== null &&
        daysUntilClose <= 7 &&
        daysUntilClose > 0
      ) {
        setAction({
          kind: 'closing',
          urgent: daysUntilClose <= 2,
          label: `Registration closes in ${daysUntilClose} day${daysUntilClose === 1 ? '' : 's'}`,
          icon: <CalendarDaysIcon className="h-3.5 w-3.5" />,
        });
        return;
      }
      // 2. Unpaid
      if (unpaidCount > 0) {
        setAction({
          kind: 'unpaid',
          urgent: false,
          label: `${unpaidCount} unpaid registration${unpaidCount === 1 ? '' : 's'}`,
          icon: <CurrencyDollarIcon className="h-3.5 w-3.5" />,
        });
        return;
      }
      // 3. Match recap due
      if (recentGame) {
        const opp = recentGame.opponent || 'opponent';
        setAction({
          kind: 'recap',
          urgent: false,
          label: `Game vs ${opp} — draft a recap?`,
          icon: <SparklesIcon className="h-3.5 w-3.5" />,
        });
        return;
      }
      setAction(null);
    };

    // Subscribe to active season
    const seasonQ = query(
      collection(db, 'seasons'),
      where('teamId', '==', teamId),
      where('status', '==', 'open'),
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    const unsubSeason = onSnapshot(seasonQ, async (snap) => {
      if (snap.empty) {
        activeSeason = null;
        unpaidCount = 0;
        daysUntilClose = null;
        recompute();
        return;
      }
      activeSeason = { id: snap.docs[0].id, ...snap.docs[0].data() };
      // Compute daysUntilClose
      if (activeSeason.registrationClose) {
        try {
          const closeDate = endOfSeasonDay(activeSeason.registrationClose);
          const days = Math.ceil((closeDate - Date.now()) / (1000 * 60 * 60 * 24));
          daysUntilClose = days > 0 ? days : 0;
        } catch {
          daysUntilClose = null;
        }
      } else {
        daysUntilClose = null;
      }
      // Get unpaid count via one-shot getDocs (no need for live)
      try {
        const regsSnap = await getDocs(query(
          collection(db, 'registrations'),
          where('teamId', '==', teamId),
          where('seasonId', '==', activeSeason.id),
        ));
        let unpaid = 0;
        regsSnap.forEach((d) => {
          const r = d.data();
          const amt = r.pricing?.finalAmount ?? r.amountDue ?? 0;
          if (amt > 0 && !['paid', 'confirmed', 'cancelled'].includes(r.status)) {
            unpaid += 1;
          }
        });
        unpaidCount = unpaid;
      } catch {
        unpaidCount = 0;
      }
      recompute();
    }, (err) => {
      // Degrade gracefully on listener errors (missing index, permission,
      // offline) instead of throwing an uncaught error to the console.
      console.warn('[TodayPanel] season listener error:', err?.code, err?.message);
      activeSeason = null;
      unpaidCount = 0;
      daysUntilClose = null;
      recompute();
    });

    // Subscribe to recent past games (for recap detection)
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000);
    const gamesQ = query(
      collection(db, 'games'),
      where('teamId', '==', teamId),
      where('eventType', '==', 'game'),
      orderBy('date', 'desc'),
      limit(3),
    );
    const unsubGames = onSnapshot(gamesQ, (snap) => {
      const now = Date.now();
      recentGame = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .find((g) => {
          const ms = g.date?.toMillis ? g.date.toMillis() : 0;
          return ms > 0 && ms < now && ms > cutoff.getTime() && g.status !== 'cancelled';
        }) || null;
      recompute();
    }, (err) => {
      // Degrade gracefully (e.g. missing index) — no recap detection
      // rather than an uncaught error in the snapshot listener.
      console.warn('[TodayPanel] games listener error:', err?.code, err?.message);
      recentGame = null;
      recompute();
    });

    return () => {
      unsubSeason();
      unsubGames();
    };
  }, [teamId]);

  return action;
}

/* ─── Compact per-team row ────────────────────────────────────── */
function TeamRow({ team, onReport }) {
  const action = useTeamUrgentAction(team.id);
  const hasAction = !!action;
  // Report up so the panel can hide its chrome when NO team has an
  // action — an empty "Today" card was pure noise (2026-07-24 audit).
  useEffect(() => {
    onReport?.(team.id, hasAction);
  }, [team.id, hasAction, onReport]);
  if (!action) return null;

  const isUrgent = action.urgent;
  return (
    <Link
      to={`/teams/${team.id}`}
      className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors group ${
        isUrgent
          ? 'border-red-200 bg-red-50 hover:bg-red-100'
          : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5'
      }`}
    >
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
        isUrgent ? 'bg-red-100 text-red-700' : 'bg-[#b5f03d]/15 text-[#5a8a1f]'
      }`}>
        {action.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-bold text-[#0d1f14] truncate"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.2px' }}
        >
          {team.name}
        </p>
        <p className={`text-xs truncate ${isUrgent ? 'text-red-700 font-medium' : 'text-zinc-600'}`}>
          {action.label}
        </p>
      </div>
      <ArrowRightIcon className="h-4 w-4 shrink-0 text-zinc-300 group-hover:text-[#5a8a1f] transition-colors" />
    </Link>
  );
}

/* ─── Public panel ────────────────────────────────────────────── */
export default function TodayPanel({ coachTeams }) {
  // Track which teams have an action so the chrome only renders when
  // there is something to say. Rows report via onReport; rows with no
  // action render null themselves.
  const [rowsWithActions, setRowsWithActions] = useState({});
  const onReport = useCallback((teamId, has) => {
    setRowsWithActions((p) => (p[teamId] === has ? p : { ...p, [teamId]: has }));
  }, []);

  const teams = useMemo(() => coachTeams || [], [coachTeams]);

  if (teams.length < 2) return null; // single-team users use Smart Bypass

  const anyAction = Object.values(rowsWithActions).some(Boolean);

  // Rows must stay MOUNTED even when the chrome is hidden — they own
  // the listeners that decide whether there's anything to show. An
  // empty "Today" header card was pure noise (2026-07-24 audit), so
  // the bordered card + header only render once a row has an action.
  const rows = teams.map((team) => (
    <TeamRow key={team.id} team={team} onReport={onReport} />
  ));

  if (!anyAction) return <div className="hidden">{rows}</div>;

  return (
    <div className="mb-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
      <div className="flex items-center gap-2 mb-3">
        <BellAlertIcon className="h-4 w-4 text-[#5a8a1f]" />
        <p
          className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
          style={{ fontFamily: HEAD_FONT }}
        >
          Today
        </p>
      </div>
      <div className="space-y-2">
        {rows}
      </div>
    </div>
  );
}
